import { useCallback, useEffect, useState } from "react";
import { FaRegFileAlt, FaUserAltSlash, FaUserCheck, FaUsers } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { convertUtcToIst } from "../../common/TimeUtils";
import { getDashboardData, setDashboardLoading } from "../../redux/services/dashboardSlice";
import Loader from "../../common/Loader";
import { FiFilter } from "react-icons/fi";
import moment from "moment-timezone";
import DateFilter from "../../common/DateFilter";
import { useUser } from "../../redux/services/authSlice";

const DashboardPage = () => {
  const dispatch = useDispatch();
  const getIncExpData = useSelector((state) => state?.dashboard?.dashboardData);
  const dashboardLoading = useSelector((state) => state?.dashboard?.dashboardLoading);
  const { user } = useUser();

  const [toggle, setToggle] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [savedStartDate, setSavedStartDate] = useState("");
  const [savedEndDate, setSavedEndDate] = useState("");

  const handleSaveDate = () => {
    setSavedStartDate(startDate);
    setSavedEndDate(endDate);
    if (handleDateSubmit) {
      handleDateSubmit();
    }
  };
  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
    setSavedStartDate("");
    setSavedEndDate("");
    if (handleDateSubmit) {
      handleDateSubmit();
    }
  };

  const topCards = [
    {
      title: "Total students",
      value: getIncExpData?.sec1?.count?.totalUser,
      icon: <FaUsers className="w-7 h-7 text-foreground" />,
      bg: "bg-purple-100",
    },
    {
      title: "Active students",
      value: getIncExpData?.sec1?.count?.userActive,
      icon: <FaUserCheck className="w-7 h-7 text-foreground" />,
      bg: "bg-blue-100",
    },
    {
      title: "Blocked students",
      value: getIncExpData?.sec1?.count?.userInactive,
      icon: <FaUserAltSlash className="w-7 h-7 text-foreground" />,
      bg: "bg-yellow-100",
    },
  ];

  const stats = [
    {
      title: "Total Workshop",
      value: getIncExpData?.sec1?.count?.workshopCount,
      icon: <FaRegFileAlt className="w-7 h-7 text-foreground" />,
      bg: "bg-purple-100",
    },
    {
      title: "Total Courses",
      value: getIncExpData?.sec1?.count?.courseCount,
      icon: <FaRegFileAlt className="w-7 h-7 text-foreground" />,
      bg: "bg-blue-100",
    },
  ];

  const handleDateSubmit = () => {
    let adjustedStartDate = null;
    if (startDate) {
      adjustedStartDate = new Date(startDate);
      adjustedStartDate.setHours(0, 0, 0, 0);
    }

    let adjustedEndDate = null;
    if (endDate) {
      adjustedEndDate = new Date(endDate);
      adjustedEndDate.setHours(23, 59, 59, 999);
    }
    const SDate = adjustedStartDate ? convertUtcToIst(adjustedStartDate) : null;
    const EDate = adjustedEndDate ? convertUtcToIst(adjustedEndDate) : null;
    if (SDate && EDate) {
      setToggle(false);
      getAllDashboardData(SDate, EDate);
      setStartDate(null);
      setEndDate(null);
    } else {
      getAllDashboardData();
    }

    return { SDate, EDate };
  };

  const getAllDashboardData = useCallback(
    async (SDate, EDate) => {
      try {
        await dispatch(setDashboardLoading(true));
        const payload = { startDate: SDate, endDate: EDate };
        await dispatch(getDashboardData(payload));
      } catch (error) {}
    },
    [dispatch]
  );

  useEffect(() => {
    if (user?.userType === "admin") {
      getAllDashboardData();
    }
  }, [getAllDashboardData, user?.userType]);

  return (
    <>
      {user?.userType === "admin" ? (
        !dashboardLoading ? (
          <div className=" space-y-6">
            {/* ===== Top Summary Cards ===== */}
            <div className="flex items-end justify-end md:flex-row flex-col-reverse gap-3">
              {savedStartDate && savedEndDate && (
                <button onClick={handleClearDates} className="inline-flex items-center space-x-2 rounded-xl px-2 py-2 text-md text-center text-white bg-primary hover:bg-opacity-90">
                  <p className="font-semibold">Clear</p>
                </button>
              )}
              <button onClick={() => setToggle(true)} className="inline-flex items-center space-x-2 rounded-xl px-2 py-2 text-md text-center text-white bg-primary hover:bg-opacity-90">
                <FiFilter className="font-bold text-white w-4 h-4" />
                <p className="font-semibold">Filter</p>
                {savedStartDate && savedEndDate && <p>({`${moment(savedStartDate).format("Do MMMM")} - ${moment(savedEndDate).format("Do MMMM")}`})</p>}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {topCards.map((item, i) => (
                <div key={i} className={`bg-card p-5 rounded-xl flex items-center justify-between shadow font-medium`}>
                  <div>
                    <p className="text-2xl font-bold">{item.value}</p>
                    <p className="text-muted-foreground text-sm">{item.title}</p>
                  </div>
                  {item.icon}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stats.map((item, i) => (
                <div key={i} className={`bg-card p-5 rounded-xl flex items-center justify-between shadow font-medium`}>
                  <div>
                    <p className="text-2xl font-bold">{item.value}</p>
                    <p className="text-muted-foreground text-sm">{item.title}</p>
                  </div>
                  {item.icon}
                </div>
              ))}
            </div>
            {toggle && (
              <DateFilter
                setToggle={setToggle}
                toggle={toggle}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                handleDateSubmit={handleSaveDate}
                startDate={startDate}
                endDate={endDate}
                onClose={() => {
                  setToggle(false);
                  handleClearDates();
                }}
              />
            )}
          </div>
        ) : (
          <Loader />
        )
      ) : (
        <div className="flex items-center justify-center p-4 w-full h-full">
          <div className="text-center animate__animated animate__fadeIn animate__delay-1s">
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-primary mb-2 sm:mb-4">No Data Found</h2>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardPage;
